import {
    INITIAL_MAP_INTERACTION_STATE,
    type LocatedFeature,
    type LocateOrigin,
    type MapInteractionState,
    type MapToolId,
    type SelectedFeature,
} from './mapInteractionState';

/**
 * Every transition of the map-interaction slice. Pure and map-free: navigation
 * (easeTo/fitBounds/flyTo) always stays with the dispatching caller, and panel
 * visibility (`setActivePanel`) plus panel-specific side effects stay in the owning
 * MapView handler — PANEL_OPENED only records the interaction-state consequence.
 */
export type MapInteractionAction =
    | { type: 'SELECT_FEATURE'; selection: SelectedFeature }
    | { type: 'LOCATE_FEATURE'; feature: LocatedFeature; origin: LocateOrigin }
    /** Details close button, stats-card close, Escape, semantic-zoom hide. */
    | { type: 'CLEAR_SELECTION' }
    | { type: 'EMPTY_MAP_CLICK' }
    /** A side panel (Layers/Filters/Download/MapKey/Stats) opened. */
    | { type: 'PANEL_OPENED' }
    | { type: 'TABLE_CLOSED'; reason: 'explicit' | 'reveal' }
    | { type: 'SEARCH_CLEARED' }
    | { type: 'CLIENT_CHANGED' }
    | { type: 'RESET_TO_NETWORK' }
    /** A map tool (radial measure) toggled on from the map controls. */
    | { type: 'TOOL_ACTIVATED'; tool: MapToolId }
    /** The active tool toggled off: button, Escape from its idle phase, or its own cleanup. */
    | { type: 'TOOL_DEACTIVATED' };

/** True when the slice already holds nothing — lets resets bail out reference-equal. */
function isEmpty(state: MapInteractionState): boolean {
    return state.selection === null && state.located === null && state.activeTool === null;
}

/**
 * Reducer for the map-interaction slice. Bail-out contract: any action that would
 * change nothing returns the SAME state reference — load-bearing because one physical
 * empty-map click reaches the reducer twice (usePoiInteractions and useFeatureSelection
 * each own a delegated click handler), and the second dispatch must not re-render.
 */
export function mapInteractionReducer(state: MapInteractionState, action: MapInteractionAction): MapInteractionState {
    switch (action.type) {
        // Selecting always replaces any located highlight — a stale Locate halo must
        // never outlive or override a fresh selection. It also deactivates any tool:
        // feature clicks are suppressed while a tool is active, so a selection arriving
        // anyway (search select, table Show Details) is an explicit change of intent.
        case 'SELECT_FEATURE':
            return { selection: action.selection, located: null, activeTool: null };

        // Locating always closes any open card/details: the highlight and the card
        // can never disagree, because they can never coexist. An explicit Locate also
        // wins over an active tool — the user has moved on to a feature workflow.
        case 'LOCATE_FEATURE':
            return { selection: null, located: { feature: action.feature, origin: action.origin }, activeTool: null };

        case 'CLEAR_SELECTION':
            return state.selection === null ? state : { ...state, selection: null };

        // One predictable dismissal rule: an empty-map click clears the selection
        // AND any located highlight, regardless of origin. Also the only dismissal path
        // for the mobile locate-reveal highlight, which survives its table close.
        // (While a tool is active MapView suppresses the empty-click dispatch — the
        // click is the tool's input — so these resets ending the tool is deliberate:
        // only client switches and Reset-to-network reach here with a tool active.)
        case 'EMPTY_MAP_CLICK':
        case 'CLIENT_CHANGED':
        case 'RESET_TO_NETWORK':
            return isEmpty(state) ? state : INITIAL_MAP_INTERACTION_STATE;

        // Opening a side panel closes the POI/van details panels but leaves an AOI/DMA
        // stats card open — the card is deliberately independent of panel exclusivity.
        // An active tool is likewise untouched: panels and tools are orthogonal.
        case 'PANEL_OPENED':
            return state.selection?.type === 'poi' || state.selection?.type === 'van'
                ? { ...state, selection: null }
                : state;

        case 'TABLE_CLOSED': {
            // The mobile Locate reveal closes the table as a side effect of showing the
            // map; the located highlight is the whole point of that close and is kept.
            if (action.reason === 'reveal') return state;
            // Explicit close (the close button or the map-control toggle — both must
            // behave identically): table-originated highlights lose their owner, and
            // any open POI details closes with the table that opened it. A
            // search-originated highlight, an AOI/DMA card and a van panel are not the
            // table's — the table cannot select a van, so its close never touches one.
            const selection = state.selection?.type === 'poi' ? null : state.selection;
            const located = state.located?.origin === 'table' ? null : state.located;
            if (selection === state.selection && located === state.located) return state;
            return { ...state, selection, located };
        }

        // Clearing search releases only what search created: a search-originated
        // highlight. Selection is untouched — it carries no provenance.
        case 'SEARCH_CLEARED':
            return state.located?.origin === 'search' ? { ...state, located: null } : state;

        // Activating a tool closes any open card/details/highlight: the tool owns the
        // map until it is dismissed, and a card left open would disagree with the
        // suppressed feature interaction behind it.
        case 'TOOL_ACTIVATED':
            // Invariant: a non-null activeTool implies selection and located are
            // already null, so re-activating the same tool changes nothing.
            return state.activeTool === action.tool
                ? state
                : { selection: null, located: null, activeTool: action.tool };

        case 'TOOL_DEACTIVATED':
            return state.activeTool === null ? state : { ...state, activeTool: null };
    }
}
